import Api from '@sendsprint/api-types';
import { CONFIG_NO_ERROR_TOAST, DURATION_10_MIN, DURATION_1_HR } from '@src/constants';
import { useAccount } from '@src/contexts';
import {
  addPaymentLinkService,
  addSettlementAccountService,
  deletePaymentLinkService,
  deleteSettlementAccountService,
  getBusinessDetailsService,
  getBusinessVerificationStatusService,
  getDashboardSummaryService,
  getPaymentLinkBasicDetailsService,
  getPaymentLinkDetailsService,
  getPaymentLinksService,
  getPaymentLinkSummaryService,
  getPrimarySettlementAccountsService,
  getProfileDetailsService,
  getReceiverCustomersService,
  getRoleSectionsPermissionsService,
  getSettlementAccountsService,
  updateBusinessDetailsService,
  updatePaymentLinkService,
  updatePaymentLinkStatusService,
  updateProfileDetailsService,
  updateSettlementAccountService,
  uploadBusinessLogoService,
  uploadProfilePhotoService
} from '@src/services/user-service';
import ClientApi from '@src/types/client';
import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';

export const useGetSettlementAccounts = (
  payload: ClientApi.User.GetSettlementAccounts.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<ClientApi.User.GetSettlementAccounts.Response['data'], AxiosError<any>>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetSettlementAccounts.Response['data'], AxiosError<any>>({
    queryKey: ['getSettlementsAccounts', payload.pageNumber, payload.pageSize],
    queryFn: () => getSettlementAccountsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useAddSettlementAccount = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.AddSettlementAccounts.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.AddSettlementAccounts.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.AddSettlementAccounts.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.AddSettlementAccounts.Request
  >('addSettlementAccount', addSettlementAccountService, options);
};

export const useUpdateSettlementAccount = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UpdateSettlementAccount.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UpdateSettlementAccount.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UpdateSettlementAccount.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UpdateSettlementAccount.Request
  >('updateSettlementAccount', updateSettlementAccountService, options);
};

export const useDeleteSettlementAccount = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.DeleteSettlementAccount.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.DeleteSettlementAccount.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.DeleteSettlementAccount.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.DeleteSettlementAccount.Request
  >('deleteSettlementAccount', deleteSettlementAccountService, options);
};

export const useGetPrimarySettlementAccounts = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<
    ClientApi.User.GetPrimarySettlementAccounts.Response['data'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>
  >
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetPrimarySettlementAccounts.Response['data'], AxiosError<any>>({
    queryKey: ['getPrimarySettlementsAccounts'],
    queryFn: () => getPrimarySettlementAccountsService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useAddPaymentLink = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.AddPaymentLink.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.AddPaymentLink.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.AddPaymentLink.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.AddPaymentLink.Request
  >('addPaymentLink', addPaymentLinkService, options);
};

export const useGetPaymentLinks = (
  payload: ClientApi.User.GetPaymentLinks.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<ClientApi.User.GetPaymentLinks.Response['data'], AxiosError<any>>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetPaymentLinks.Response['data'], AxiosError<any>>({
    queryKey: ['getPaymentLinks', payload],
    queryFn: () => getPaymentLinksService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useUpdatePaymentLinkStatus = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UpdatePaymentLinkStatus.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UpdatePaymentLinkStatus.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UpdatePaymentLinkStatus.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UpdatePaymentLinkStatus.Request
  >('updatePaymentLinkStatus', updatePaymentLinkStatusService, options);
};

export const useUpdatePaymentLink = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UpdatePaymentLink.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UpdatePaymentLink.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UpdatePaymentLink.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UpdatePaymentLink.Request
  >('updatePaymentLink', updatePaymentLinkService, options);
};

export const useDeletePaymentLink = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.DeletePaymentLink.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.DeletePaymentLink.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.DeletePaymentLink.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.DeletePaymentLink.Request
  >('deletePaymentLink', deletePaymentLinkService, options);
};

export const useGetPaymentLinkDetails = (
  payload: ClientApi.User.GetPaymentLinkDetails.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<ClientApi.User.GetPaymentLinkDetails.Response, AxiosError<any>>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetPaymentLinkDetails.Response, AxiosError<any>>({
    queryKey: ['getPaymentLinkDetails', payload.Reference],
    queryFn: () => getPaymentLinkDetailsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useGetPaymentLinkBasicDetails = (
  payload: ClientApi.User.GetPaymentLinkBasicDetails.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<ClientApi.User.GetPaymentLinkBasicDetails.Response, AxiosError<any>>
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetPaymentLinkBasicDetails.Response, AxiosError<any>>({
    queryKey: ['getPaymentLinkBasicDetails', payload.Reference],
    queryFn: () => getPaymentLinkBasicDetailsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useGetRoleSectionsPermissions = (
  payload: ClientApi.User.GetRoleSectionsPermissions.Request,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<
    ClientApi.User.GetRoleSectionsPermissions.Response['data'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>
  >
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetRoleSectionsPermissions.Response['data'], AxiosError<any>>({
    queryKey: ['getRoleSectionsPermissions', payload.RoleId],
    queryFn: () => getRoleSectionsPermissionsService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useGetProfileDetails = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<
    ClientApi.User.GetProfileDetails.Response['data'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>
  >
) => {
  // const { handleProfileUpdate } = useAccount();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetProfileDetails.Response['data'], AxiosError<any>>({
    queryKey: ['getProfileDetails'],
    queryFn: () => getProfileDetailsService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    onSuccess: (data) => {
      // handleProfileUpdate(data);
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
    ...options
  });
};

export const useUpdateProfileDetails = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UpdateProfileDetails.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UpdateProfileDetails.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UpdateProfileDetails.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UpdateProfileDetails.Request
  >('updateProfileDetails', updateProfileDetailsService, options);
};

export const useUploadProfilePhoto = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UploadProfilePhoto.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UploadProfilePhoto.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UploadProfilePhoto.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UploadProfilePhoto.Request
  >('uploadProfilePhoto', uploadProfilePhotoService, options);
};

export const useGetBusinessDetails = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<
    ClientApi.User.GetBusinessDetails.Response['data'],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>
  >
) => {
  const { handleBusinessUpdate } = useAccount();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.User.GetBusinessDetails.Response['data'], AxiosError<any>>({
    queryKey: ['getBusinessDetails'],
    queryFn: () => getBusinessDetailsService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    onSuccess: (data) => {
      handleBusinessUpdate(data);
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
    ...options
  });
};

export const useUpdateBusinessDetails = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UpdateBusinessDetails.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UpdateBusinessDetails.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UpdateBusinessDetails.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UpdateBusinessDetails.Request
  >('uodateBusinessDetails', updateBusinessDetailsService, options);
};

export const useUploadBusinessLogo = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.User.UploadBusinessLogo.Response['data'],
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.User.UploadBusinessLogo.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.User.UploadBusinessLogo.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.User.UploadBusinessLogo.Request
  >('uploadBusinessLogo', uploadBusinessLogoService, options);
};

export const useGetReceiverCustomers = (
  payload: ClientApi.User.GetReceiverCustomers.Request,
  options?: UseQueryOptions<
    ClientApi.User.GetReceiverCustomers.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.User.GetReceiverCustomers.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getReceiverCustomers', isLoggedIn, payload],
    queryFn: () => getReceiverCustomersService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetBusinessVerificationStatus = (
  options?: UseQueryOptions<
    ClientApi.User.GetBusinessVerificationStatus.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.User.GetBusinessVerificationStatus.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getBusinessVerificationStatus', isLoggedIn],
    queryFn: () => getBusinessVerificationStatusService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    keepPreviousData: true,
    ...options
  });
};

export const useGetPaymentLinkSummary = (
  options?: UseQueryOptions<
    ClientApi.User.GetPaymentLinkSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.User.GetPaymentLinkSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getPaymentLinkSummary', isLoggedIn],
    queryFn: () => getPaymentLinkSummaryService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    ...options
  });
};

export const useGetDashboardSummary = (
  payload: ClientApi.User.GetDashboardSummary.Request,
  options?: UseQueryOptions<
    ClientApi.User.GetDashboardSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >
) => {
  const { isLoggedIn } = useAccount();

  return useQuery<
    ClientApi.User.GetDashboardSummary.Response['data'],
    AxiosError<Api.Endpoint.Response.ErrorResponse>
  >({
    queryKey: ['getDashboardSummary', isLoggedIn, payload],
    queryFn: () => getDashboardSummaryService(payload, CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_1_HR,
    ...options
  });
};
